import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { usePayrollBundlePageContext } from 'modules/payroll_bundle/components/PayrollBundle'
import { useBulkImportSupplements } from 'modules/payroll_bundle/lib/bulkImportSupplementsContext'
import { RESET_DATA } from 'modules/payroll_bundle/lib/constants'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import supplementsCollection from 'modules/payroll_bundle/store/collections/supplements'
import SupplementBulkImport from 'modules/payroll_bundle/store/models/SupplementBulkImport'

import buildPayloadForSupplementBulkImport from './buildPayloadForSupplementBulkImport'

interface Props {
  nonZeroSupplementsCount: number
  onClose: () => void
}

const ConfirmImportModal = ({ nonZeroSupplementsCount, onClose }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const payrollBundleContext = usePayrollBundlePageContext()
  const { selectedPolicy, policyPeriod: ctxPolicyPeriod } = payrollBundleContext

  const {
    filename,
    importedSupplements,
    selectedSupplements,
    supplementTypes,
    supplementDispatch,
  } = useBulkImportSupplements()
  const [importing, setImporting] = useState(false)
  const { currentPolicyPeriods, periodIndex, currentPeriodStatus } = usePolicyPeriods()
  const {
    access: { id: accessId },
  } = useCurrentSession()

  const importData = async () => {
    if (importing) return

    setImporting(true)

    const supplementBulkImport = new SupplementBulkImport({})
    const policyPeriod = currentPolicyPeriods.length > 0 ? currentPolicyPeriods[periodIndex] : null
    const effectiveOn = stringToDate(policyPeriod?.get('starts_on'), 'backend')
    const data = buildPayloadForSupplementBulkImport({
      filename,
      authorId: accessId,
      importedSupplements,
      selectedSupplements,
      supplementTypes,
      policyPeriodId: policyPeriod?.id,
      effectiveOn,
    })

    const upload = () => {
      return supplementBulkImport.rpc('', data)
    }

    const { success } = await asyncAction(upload, {
      successMessage: i18n.t(
        'payroll_bundle.supplements.bulk_import.review_and_import.success_message'
      ),
      errorMessage: i18n.t(
        'payroll_bundle.supplements.bulk_import.review_and_import.error_message'
      ),
    })

    if (success) {
      await supplementsCollection.fetch({
        data: { policy_period_ids: [policyPeriod?.attributes.id] },
      })

      supplementDispatch({
        type: RESET_DATA,
      })

      track(trackingEvents.payroll.confirmImportSupplement, {
        period_status: currentPeriodStatus,
        number_of_supplement_types: [
          ...new Set(data.supplements.map((item) => item.contracts_taxonomy_id)),
        ].length,
        number_of_supplement_rows: data.supplements.length,
      })

      navigate(
        navigation.payrollBundle.policy.period.cycles.supplements.fullPath({
          policyId: selectedPolicy?.id,
          periodId: ctxPolicyPeriod?.id,
        })
      )
    }

    setImporting(false)
  }

  const title = i18n.t('payroll_bundle.import_supplements.review_and_import.confirm_modal.title')

  const description = (
    <Box gap="s24">
      <Text.Normal>
        {i18n.tView(
          'payroll_bundle.import_supplements.review_and_import.confirm_modal.description',
          {
            bold_text: (
              <strong>
                {i18n.t(
                  'payroll_bundle.import_supplements.review_and_import.confirm_modal.description_emphasis'
                )}
              </strong>
            ),
          }
        )}
      </Text.Normal>
    </Box>
  )

  const footer = (
    <>
      <Button type="secondary" onClick={onClose}>
        {i18n.t('common.cancel')}
      </Button>
      <Button submit disabled={nonZeroSupplementsCount === 0 || importing}>
        {i18n.tp(
          'payroll_bundle.import_supplements.review_and_import.confirm_modal.confirm_button',
          {
            count: nonZeroSupplementsCount,
          }
        )}
      </Button>
    </>
  )

  return (
    <Modal
      illustration="document-edit"
      title={title}
      description={description}
      footer={footer}
      onClose={onClose}
      onSubmit={importData}
    />
  )
}

export default observer(ConfirmImportModal)
